import React from 'react';
import PropTypes from 'prop-types';

import styled, { css } from 'styled-components';

const ProgressBar = styled.input`
    width: 100%;
    height: 16px;
    -webkit-appearance: none;
    outline: none;
    border: 0; /* for firefox on android */
    cursor: pointer;
    background-color: transparent;

    ${(props) => {
        return css`
            &::-webkit-slider-runnable-track {
                background: linear-gradient(
                    to right,
                    var(--gray700) 0%,
                    var(--gray700) ${props.value}%,
                    var(--gray200) ${props.value}%,
                    var(--gray200) 100%
                );
                height: 2px;
                border-radius: 2px;
            }

            &:hover::-webkit-slider-runnable-track {
                background: linear-gradient(
                    to right,
                    var(--blue500) 0%,
                    var(--blue500) ${props.value}%,
                    var(--gray200) ${props.value}%,
                    var(--gray200) 100%
                );
            }
        `;
    }}

    &:focus::-webkit-slider-runnable-track {
        box-shadow: var(--outline);
    }

    &:active::-webkit-slider-runnable-track {
        box-shadow: none;
    }

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 8px;
        height: 8px;
        background-color: var(--color-white);
        border-radius: 50%;
        box-shadow: 0 0 2px 2px var(--blue200);
        margin-top: -3px;
        cursor: pointer;
        transition: 0.3s;
    }

    &:hover::-webkit-slider-thumb {
        background-color: var(--blue500);
    }

    &:focus::-webkit-slider-thumb {
    }

    /** Mozilla */

    &::-moz-focus-outer {
        border: 0;
    }

    &::-moz-range-track {
        background-color: var(--gray200);
        height: 2px;
        border-radius: 2px;
    }

    &:hover::-moz-range-track {
    }

    &:focus::-moz-range-track {
        box-shadow: var(--outline);
    }

    &:active::-moz-range-track {
        box-shadow: none;
    }

    &::-moz-range-thumb {
        width: 8px;
        height: 8px;
        background-color: var(--color-white);
        box-shadow: 0 0 2px 2px var(--blue200);
        border-radius: 50%;
        cursor: pointer;
    }

    &:hover::-moz-range-thumb,
    &:focus::-moz-range-thumb {
        background-color: var(--blue500);
    }

    &::-moz-range-progress {
        height: 2px;
        border-radius: 2px;
        background-color: var(--gray700);
    }

    &:hover::-moz-range-progress,
    &:focus::-moz-range-progress {
        background-color: var(--blue500);
    }

    /** Edge and IE */

    &::-ms-track {
        background-color: var(--gray200);
        height: 2px;
        border-radius: 2px;
        border: none;
        color: transparent;
    }

    &:hover::-ms-track {
    }
    &:focus::-ms-track {
        box-shadow: var(--outline);
    }
    &:active::-ms-track {
        box-shadow: none;
    }

    &::-ms-thumb {
        width: 8px;
        height: 8px;
        background-color: var(--color-white);
        border-radius: 50%;
        border: none;
        box-shadow: 0 0 2px 2px var(--blue200);
        /* cursor: pointer doesnt work */
    }

    &:hover::-ms-thumb,
    &:focus::-ms-thumb {
        background-color: var(--blue500);
    }

    &::-ms-fill-lower {
        background-color: var(--gray700);
        border-radius: 2px;
    }

    &:hover::-ms-fill-lower {
        background-color: var(--blue500);
    }
    &:focus::-ms-fill-lower {
    }

    &::-ms-tooltip {
        display: none;
    }
`;

const ProgressControl = ({ progress = 0, onChange, keyboardHandler = () => {}, ariaLabel, ariaControls }) => {
    return (
        <ProgressBar
            onChange={(event) => {
                onChange(event.target.value);
            }}
            onClick={(event) => event.target.blur()}
            onKeyDown={(event) => keyboardHandler(event)}
            type="range"
            min="0"
            max="100"
            value={progress}
            aria-label={ariaLabel}
            aria-controls={ariaControls}
            aria-valuemax="100"
            aria-valuemin="0"
            aria-valuenow={progress}
            role="slider"
        />
    );
};

ProgressControl.propTypes = {
    progress: PropTypes.number,
    onChange: PropTypes.func,
    keyboardHandler: PropTypes.func,
    ariaLabel: PropTypes.string.isRequired,
    ariaControls: PropTypes.string.isRequired,
};

export default ProgressControl;
